/* import __COLOCATED_TEMPLATE__ from './fin-readiness-qa-section.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import { taskFor } from 'ember-concurrency-ts';
import { loadMessengerSettings } from 'embercom/lib/home/load-messenger-settings';
import { task } from 'ember-concurrency-decorators';
import { tracked } from '@glimmer/tracking';
import EmberObject, { action } from '@ember/object';
import type ThemeService from 'embercom/services/theme-service';
import type IntlService from 'embercom/services/intl';
import { textToBlocks } from '../lib/text-to-blocks';
import { assetUrl } from '@intercom/pulse/helpers/asset-url';
import { schedule } from '@ember/runloop';

type TabType = 'informational' | 'personalized-tasks' | 'handoff';

interface Args {
  industryOptions: any[];
  selectedIndustry: string;
  onIndustryChange: (selectedItem: string) => void;
  shouldShowPersonalized: boolean;
}

export default class FinReadinessQaSection extends Component<Args> {
  @service declare appService: $TSFixMe;
  @service declare store: Store;
  @service declare contentSuggestionsService: ContentSuggestionsService;
  @service declare themeService: ThemeService;
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;
  @tracked messengerSettings: any;
  @tracked operatorIdentity: any;
  @tracked _personalizedInformationalQAPairs?: any[] = undefined;
  @tracked selectedTab: TabType = 'informational';
  @tracked selectedInformationalQAPair: any;
  @tracked selectedPersonalizedQAPair: any;
  @tracked selectedHandOffQAPair: any;

  constructor(owner: any, args: any) {
    super(owner, args);
    taskFor(this.loadMessengerSettings).perform();
    taskFor(this.loadOperatorIdentity).perform();
  }

  @task({ drop: true })
  *loadMessengerSettings(): Generator<any, void, any> {
    this.messengerSettings = yield loadMessengerSettings(this);
    this.messengerSettings.previewConfiguration.isFinPreview = true;
  }

  @task({ drop: true })
  *loadOperatorIdentity(): Generator<any, void, any> {
    this.operatorIdentity = yield this.store.findRecord('operator-identity', 1);
  }

  @task({ drop: true })
  *qaPairsLoader(): Generator<any, any, any> {
    this._personalizedInformationalQAPairs = yield taskFor(
      this.contentSuggestionsService.loadQuestionAnswerPairsForGettingStarted,
    ).perform();
    this.selectedInformationalQAPair = this._personalizedInformationalQAPairs?.[0];
  }

  get personalizedInformationalQAPairs() {
    if (this._personalizedInformationalQAPairs === undefined) {
      if (this.args.shouldShowPersonalized) {
        schedule('afterRender', () => {
          taskFor(this.qaPairsLoader).perform();
        });
      }
    }
    return this._personalizedInformationalQAPairs;
  }

  get finIdentity() {
    let identity = this.operatorIdentity.getProperties(
      'name',
      'isOperator',
      'avatarData',
      'avatarType',
    );
    identity = EmberObject.create(identity);
    identity.set('avatarData', {
      url: this.operatorIdentity.avatar.image_urls.square_128,
      avatarShape: this.operatorIdentity.avatarData.avatarShape,
    });
    return identity;
  }

  get anaIdentity() {
    return EmberObject.create({
      nameOrEmail: this.intl.t(
        'automation.ai-insights.fin-readiness.see-fin-in-action.handoff.question-two.conversation.admin-name',
      ),
      avatarData: {
        url: assetUrl('/assets/images/automation/fin-readiness/avatar.svg'),
        avatarShape: 'circle',
        isTeammate: true,
      },
    });
  }

  get tabButtons() {
    return [
      {
        value: 'informational',
        label: this.intl.t(
          'automation.ai-insights.fin-readiness.see-fin-in-action.tabs.informational',
        ),
        colors: ['bg-vis-violet-50'],
      },
      {
        value: 'personalized-tasks',
        label: this.intl.t(
          'automation.ai-insights.fin-readiness.see-fin-in-action.tabs.personalized-tasks',
        ),
        colors: ['bg-vis-violet-60', 'bg-vis-violet-70'],
      },
      {
        value: 'handoff',
        label: this.intl.t('automation.ai-insights.fin-readiness.see-fin-in-action.tabs.handoff'),
        colors: [],
      },
    ];
  }

  get displayedQAPairs() {
    if (this.selectedTab === 'handoff') {
      return this.handoffQAPairs;
    }
    if (this.selectedTab === 'personalized-tasks') {
      return this.personalizedQAPairs;
    }
    if (this.args.shouldShowPersonalized) {
      return this.personalizedInformationalQAPairs || [];
    }
    return this.genericInformationalQAPairs;
  }

  get isLoading() {
    return (
      taskFor(this.loadMessengerSettings).isRunning ||
      taskFor(this.loadOperatorIdentity).isRunning ||
      taskFor(this.qaPairsLoader).isRunning
    );
  }

  get conversationParts() {
    if (this.selectedTab === 'handoff') {
      return this.getHandoffConversation();
    }

    if (this.selectedTab === 'personalized-tasks') {
      return this.getPersonalisedConversation();
    }

    return this.getInformationalConversation();
  }

  get selectedQAPair() {
    if (this.selectedTab === 'handoff') {
      return this.selectedHandOffQAPair || this.handoffQAPairs[0];
    } else if (this.selectedTab === 'personalized-tasks') {
      let currentPairs = this.personalizedQAPairs;
      let isValidSelection =
        this.selectedPersonalizedQAPair &&
        currentPairs.some(
          (pair) =>
            pair.question === this.selectedPersonalizedQAPair?.question &&
            pair.answer === this.selectedPersonalizedQAPair?.answer,
        );
      return isValidSelection ? this.selectedPersonalizedQAPair : currentPairs[0];
    } else {
      return this.selectedInformationalQAPair || this.displayedQAPairs[0];
    }
  }

  @action
  selectQAPair(QAPair: any) {
    if (this.selectedTab === 'handoff') {
      this.selectedHandOffQAPair = QAPair;
    } else if (this.selectedTab === 'personalized-tasks') {
      this.selectedPersonalizedQAPair = QAPair;
    } else {
      this.selectedInformationalQAPair = QAPair;
    }

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'see_fin_in_action_question',
      place: 'get-started',
      section: 'see_fin_in_action',
      context: 'automation.get-started.see-fin-in-action',
      tab: this.selectedTab,
      question: QAPair?.question,
      is_personalized: this.args.shouldShowPersonalized,
    });
  }

  @action
  selectTab(tab: TabType) {
    this.selectedTab = tab;

    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'see_fin_in_action_tab',
      place: 'get-started',
      section: 'see_fin_in_action',
      context: 'automation.get-started.see-fin-in-action',
      tab,
      is_personalized: this.args.shouldShowPersonalized,
    });

    if (tab === 'handoff') {
      this.selectedHandOffQAPair = this.selectedQAPair;
    } else if (tab === 'personalized-tasks') {
      this.selectedPersonalizedQAPair = this.selectedQAPair;
    } else {
      this.selectedInformationalQAPair = this.selectedQAPair;
    }
  }

  @action
  onIndustryChange(selectedItem: string) {
    this.args.onIndustryChange(selectedItem);

    this.selectedPersonalizedQAPair = this.personalizedQAPairs[0];
  }

  @action
  selectPersonalizedQAPair(pair: any) {
    this.selectedPersonalizedQAPair = pair;
  }

  @action
  is_pair_selected(pair: { question: string; answer: string }) {
    return (
      this.selectedQAPair?.question === pair.question && this.selectedQAPair?.answer === pair.answer
    );
  }

  //Static handoff data
  get handoffQAPairs() {
    return [
      {
        question: this.intl.t(
          'automation.ai-insights.fin-readiness.see-fin-in-action.handoff.question-one.title',
        ),
        answer: this.intl.t(
          'automation.ai-insights.fin-readiness.see-fin-in-action.handoff.question-one.description',
        ),
      },
      {
        question: this.intl.t(
          'automation.ai-insights.fin-readiness.see-fin-in-action.handoff.question-two.title',
        ),
        answer: this.intl.t(
          'automation.ai-insights.fin-readiness.see-fin-in-action.handoff.question-two.description',
        ),
      },
      {
        question: this.intl.t(
          'automation.ai-insights.fin-readiness.see-fin-in-action.handoff.question-three.title',
        ),
        answer: this.intl.t(
          'automation.ai-insights.fin-readiness.see-fin-in-action.handoff.question-three.description',
        ),
      },
    ];
  }

  get personalizedQAPairs() {
    let baseKey = `automation.ai-insights.fin-readiness.personalized-conversations.${this.args.selectedIndustry}`;
    let suffixes = ['one', 'two', 'three', 'four', 'five', 'six'];

    return suffixes.map((suffix) => ({
      question: this.intl.t(`${baseKey}.question_${suffix}.conversation.user-question`),
      answer: this.intl.t(`${baseKey}.question_${suffix}.conversation.bot-response`),
    }));
  }

  get genericInformationalQAPairs() {
    let baseKey = `automation.ai-insights.fin-readiness.informational-conversations.${this.args.selectedIndustry}`;
    let suffixes = ['one', 'two', 'three', 'four', 'five', 'six'];

    return suffixes.map((suffix) => ({
      question: this.intl.t(`${baseKey}.question_${suffix}.conversation.user-question`),
      answer: this.intl.t(`${baseKey}.question_${suffix}.conversation.bot-response`),
    }));
  }

  get currentPersonalizedConversationParts() {
    let currentPair = this.selectedPersonalizedQAPair;
    if (!currentPair) {
      return [];
    }

    return [
      this.createConversationPart(
        this.intl.t('automation.ai-insights.fin-readiness.initial-message'),
        {
          initialMessage: true,
        },
      ),
      this.createConversationPart(currentPair?.question),
      this.createConversationPart(currentPair?.answer),
    ];
  }

  // Helper to create a conversation part
  private createConversationPart(text: string, options: any = {}) {
    return {
      app_id: this.appService.app.id,
      participant_id:
        options.isBot || options.admin || options.initialMessage
          ? undefined
          : 'test-participant-id',
      isBotPart: options.isBot || false,
      sub_type: options.subType || 'comment',
      type: options.type,
      admin: options.admin,
      blocks: options.isHtml ? [{ type: 'html', content: text }] : textToBlocks(text),
    };
  }

  private getInformationalConversation() {
    let currentPair = this.selectedQAPair;

    if (this.args.shouldShowPersonalized) {
      return [
        this.createConversationPart(
          this.intl.t('automation.ai-insights.fin-readiness.initial-message'),
          {
            initialMessage: true,
          },
        ),
        this.createConversationPart(currentPair?.question),
        this.createConversationPart(currentPair?.answer, { isBot: true }),
      ];
    }

    let questionIndex = this.displayedQAPairs.findIndex(
      (pair) => pair.question === currentPair?.question && pair.answer === currentPair?.answer,
    );

    // If not found, default to first question
    if (questionIndex === -1) {
      questionIndex = 0;
    }
    let suffixes = ['one', 'two', 'three', 'four', 'five', 'six'];
    let questionKey = suffixes[questionIndex] || 'one';

    let baseKey = `automation.ai-insights.fin-readiness.informational-conversations.${this.args.selectedIndustry}.question_${questionKey}.conversation`;
    let conversationParts = [
      this.createConversationPart(
        this.intl.t(
          `automation.ai-insights.fin-readiness.informational-conversations.initial-message`,
        ),
        { initialMessage: true },
      ),
      this.createConversationPart(this.intl.t(`${baseKey}.user-question`)),
      this.createConversationPart(this.intl.t(`${baseKey}.bot-response`), { isBot: true }),
    ];

    // Add follow-up messages if they exist
    if (this.intl.exists(`${baseKey}.user-follow-up`)) {
      conversationParts.push(this.createConversationPart(this.intl.t(`${baseKey}.user-follow-up`)));
    }
    if (this.intl.exists(`${baseKey}.bot-follow-up`)) {
      conversationParts.push(
        this.createConversationPart(this.intl.t(`${baseKey}.bot-follow-up`), { isBot: true }),
      );
    }
    if (this.intl.exists(`${baseKey}.user-follow-up-2`)) {
      conversationParts.push(
        this.createConversationPart(this.intl.t(`${baseKey}.user-follow-up-2`)),
      );
    }
    if (this.intl.exists(`${baseKey}.bot-follow-up-2`)) {
      conversationParts.push(
        this.createConversationPart(this.intl.t(`${baseKey}.bot-follow-up-2`), { isBot: true }),
      );
    }

    return conversationParts;
  }

  private getHandoffConversation() {
    let currentPair = this.selectedQAPair;
    let questionIndex = this.displayedQAPairs.findIndex(
      (pair) => pair.question === currentPair?.question && pair.answer === currentPair?.answer,
    );
    // findIndex returns -1 when no match is found
    if (questionIndex === -1) {
      questionIndex = 0;
    }
    let questionNumber = questionIndex + 1;

    let questionKey;
    if (questionNumber === 1) {
      questionKey = 'one';
    } else if (questionNumber === 2) {
      questionKey = 'two';
    } else {
      questionKey = 'three';
    }
    let baseKey = `automation.ai-insights.fin-readiness.see-fin-in-action.handoff.question-${questionKey}.conversation`;

    switch (questionNumber) {
      case 1: // No confidence question
        return [
          this.createConversationPart(this.intl.t(`${baseKey}.user-question`)),
          this.createConversationPart(
            `
              <p>${this.intl.t(`${baseKey}.bot-response-1`)}</p>
              <br>
              <p>${this.intl.t(`${baseKey}.bot-response-2`)}</p>
              <br>
              <p>${this.intl.t(`${baseKey}.bot-response-3-intro`)}</p>
              <ul>
                <li>${this.intl.t(`${baseKey}.bot-response-3-option-1`)}</li>
                <li>${this.intl.t(`${baseKey}.bot-response-3-option-2`)}</li>
                <li>${this.intl.t(`${baseKey}.bot-response-3-option-3`)}</li>
              </ul>
            `,
            { isBot: true, isHtml: true },
          ),
        ];

      case 2: // Guided escalation question
        return [
          this.createConversationPart(this.intl.t(`${baseKey}.user-question`)),
          this.createConversationPart(this.intl.t(`${baseKey}.assignment-message`), {
            subType: 'assignment',
            type: 'comment',
          }),
          this.createConversationPart(this.intl.t(`${baseKey}.admin-response`), {
            admin: this.anaIdentity,
          }),
        ];

      default: // Customer asks for a human
        return [
          this.createConversationPart(this.intl.t(`${baseKey}.user-question`)),
          this.createConversationPart(
            `
            <p>${this.intl.t(`${baseKey}.bot-response-intro`)}</p>
            <ol>
              <li>${this.intl.t(`${baseKey}.bot-instruction-1`)}</li>
              <li>${this.intl.t(`${baseKey}.bot-instruction-2`)}</li>
              <li>${this.intl.t(`${baseKey}.bot-instruction-3`)}</li>
              <li>${this.intl.t(`${baseKey}.bot-instruction-4`)}</li>
            </ol>
          `,
            {
              isBot: true,
              isHtml: true,
            },
          ),
          this.createConversationPart(this.intl.t(`${baseKey}.user-follow-up`)),
          this.createConversationPart(this.intl.t(`${baseKey}.bot-handoff-message`), {
            isBot: true,
          }),
          this.createConversationPart(this.intl.t(`${baseKey}.admin-response`), {
            admin: this.anaIdentity,
          }),
        ];
    }
  }

  private getPersonalisedConversation() {
    let currentPair = this.selectedQAPair;
    let questionIndex = this.displayedQAPairs.findIndex(
      (pair) => pair.question === currentPair?.question && pair.answer === currentPair?.answer,
    );

    // If not found, default to first question
    if (questionIndex === -1) {
      questionIndex = 0;
    }
    let suffixes = ['one', 'two', 'three', 'four', 'five', 'six'];
    let questionKey = suffixes[questionIndex] || 'one';

    let baseKey = `automation.ai-insights.fin-readiness.personalized-conversations.${this.args.selectedIndustry}.question_${questionKey}.conversation`;

    let conversationParts = [
      this.createConversationPart(
        this.intl.t(
          `automation.ai-insights.fin-readiness.personalized-conversations.initial-message`,
        ),
        { initialMessage: true },
      ),
      this.createConversationPart(this.intl.t(`${baseKey}.user-question`)),
      this.createConversationPart(this.intl.t(`${baseKey}.bot-response`), { isBot: true }),
    ];

    // Add follow-up messages if they exist
    if (this.intl.exists(`${baseKey}.user-follow-up`)) {
      conversationParts.push(this.createConversationPart(this.intl.t(`${baseKey}.user-follow-up`)));
    }
    if (this.intl.exists(`${baseKey}.bot-follow-up`)) {
      conversationParts.push(
        this.createConversationPart(this.intl.t(`${baseKey}.bot-follow-up`), { isBot: true }),
      );
    }
    if (this.intl.exists(`${baseKey}.user-follow-up-2`)) {
      conversationParts.push(
        this.createConversationPart(this.intl.t(`${baseKey}.user-follow-up-2`)),
      );
    }
    if (this.intl.exists(`${baseKey}.bot-follow-up-2`)) {
      conversationParts.push(
        this.createConversationPart(this.intl.t(`${baseKey}.bot-follow-up-2`), { isBot: true }),
      );
    }

    return conversationParts;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::FinReadiness::FinReadinessQaSection': typeof FinReadinessQaSection;
  }
}
